'use client';

import type { MouseEvent } from 'react';
import { CalendarDays, MapPin, ChevronRight, Ticket, Users } from 'lucide-react';
import type { FinderDate, FinderEvent, WayIn } from './types';
import { campusLabel, campusShort, fmtMoney, fmtPrice, fmtWhen, pluralDates } from './format';
import { toPlainText } from './richText';
import { CampusIcon } from './CampusIcon';
import { Pill, Tag } from './chips';

interface EventCardProps {
  event: FinderEvent;
  /** The dates that survived the filters, soonest first */
  dates: FinderDate[];
  detailsUrl: string;
  /**
   * Opens the event in a sheet (the finder). When omitted the whole card is
   * a link to the details page (search results and other listings).
   */
  onOpen?: (event: FinderEvent, dates: FinderDate[]) => void;
}

const TAG_ICON = 'h-3.5 w-3.5 text-gray-500';

/** The venue on one line: "Troy Campus", "Virtual", "Camp Timber Wolf Lake, Lake City". */
export function whereLine(e: FinderEvent): string {
  const venue = e.locationName ?? campusLabel(e.congregationId, e.congregationName);
  if (e.offsite && e.city && !venue.toLowerCase().includes(e.city.toLowerCase())) {
    return `${venue}, ${e.city}`;
  }
  return venue;
}

/** The venue's postal address, one entry per line, without the venue name. */
export function addressLines(e: FinderEvent): string[] {
  const lines = [e.addressLine1, e.addressLine2].filter((l): l is string => Boolean(l));
  const cityLine = [e.city, [e.state, e.postalCode].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');
  if (cityLine) lines.push(cityLine);
  return lines;
}

/** Art for a card or sheet: the date's own poster, else the series', else a brand tile. */
export function EventArt({
  event,
  date,
  className = '',
  sizes,
}: {
  event: FinderEvent;
  date?: FinderDate;
  className?: string;
  sizes?: string;
}) {
  const src = date?.imageUrl ?? event.imageUrl;
  if (src) {
    return (
      <img
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        sizes={sizes}
        className={`h-full w-full object-cover ${className}`}
        draggable={false}
      />
    );
  }
  return (
    <div
      className={`flex h-full w-full items-center justify-center ${className}`}
      style={{ background: 'linear-gradient(135deg, #1c2b39 0%, #243848 50%, #2d4a5c 100%)' }}
    >
      <CalendarDays className="h-12 w-12 text-[#4cb8ab]" strokeWidth={1.5} />
    </div>
  );
}

/**
 * How many ways in a card names before it starts counting them. Four covers
 * every event that has them today and keeps the chips to two rows.
 */
const WAYS_ON_CARD = 4;

/**
 * One way in as a chip: "Student $10", "Leader", "Volunteer".
 *
 * Free is the absence of a price, not the word. A chip that says Free is a
 * chip spending its width on nothing, and a row of them buries the one that
 * costs $10. The label is what a person is reading for anyway: whether there
 * is a door with their name on it.
 */
export function WayInTag({ way }: { way: WayIn }) {
  const paid = way.price > 0;
  const icon = way.serving ? (
    <Users className={TAG_ICON} />
  ) : paid ? (
    <Ticket className={TAG_ICON} />
  ) : undefined;
  return <Tag icon={icon}>{paid ? `${way.label} ${fmtMoney(way.price)}` : way.label}</Tag>;
}

/**
 * What the ways in sit under, so they are not read as more traits.
 *
 * "Church Wide" and "Special Event" describe the event. "Leader" and "Student
 * Ministry Staff" are choices a person makes about themselves, and in one
 * undifferentiated row of chips they read as the first kind. A rule and a
 * quiet label cost two lines and settle it.
 */
function WaysHeading() {
  return (
    <span className="text-[10px] font-bold tracking-widest text-gray-500 uppercase">
      Register as
    </span>
  );
}

/**
 * The chips under a card: what the event IS, then how to get into it.
 *
 * `showWaysIn` is false where the ways in already have a section of their own
 * (the finder's sheet), so the same facts are not printed twice on one screen.
 */
export function EventTags({
  event,
  showWaysIn = true,
}: {
  event: FinderEvent;
  showWaysIn?: boolean;
}) {
  const ways = event.waysIn ?? [];
  // MP's one price per event is the right answer only when there is one way
  // in. Where staff named several, that number is the Product's $0.
  const price = ways.length === 0 ? fmtPrice(event.basePrice, event.productId !== null) : null;
  const shown = ways.slice(0, WAYS_ON_CARD);
  // A way in that says "volunteer" already says it better than a count of MP
  // Opportunities, and the card has room for one of the two. Suppressed
  // whenever such a way in exists, shown or not, because where it is not
  // shown there is a line above saying the same thing.
  const serveTag = event.opportunityCount > 0 && !ways.some((w) => w.serving);
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap gap-1.5">
        <Tag icon={<CampusIcon congregationId={event.congregationId} className="h-4 w-4" />}>
          {campusShort(event.congregationName)}
        </Tag>
        {event.eventType && <Tag>{event.eventType}</Tag>}
        {price && price !== 'Free' && <Tag icon={<Ticket className={TAG_ICON} />}>{price}</Tag>}
        {serveTag && (
          <Tag icon={<Users className={TAG_ICON} />}>
            {event.opportunityCount === 1
              ? '1 serving role'
              : `${event.opportunityCount} serving roles`}
          </Tag>
        )}
      </div>
      {showWaysIn && shown.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-gray-100 pt-2.5">
          <WaysHeading />
          <div className="flex flex-wrap gap-1.5">
            {shown.map((way) => (
              <WayInTag key={way.label} way={way} />
            ))}
            {ways.length > shown.length && <Tag>+{ways.length - shown.length} more</Tag>}
          </div>
        </div>
      )}
    </div>
  );
}

/** The pills that ride on the artwork: Featured and Full. The date line and the CTA say the rest. */
export function StatusPills({ date }: { event: FinderEvent; date: FinderDate }) {
  return (
    <>
      {date.featured && <Pill tone="teal">Featured</Pill>}
      {date.full && <Pill tone="amber">Full</Pill>}
    </>
  );
}

const CARD =
  'group flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white text-left text-inherit no-underline shadow-sm transition-all hover:scale-[1.01] hover:shadow-md active:scale-[0.995]';

export function EventCard({ event, dates, detailsUrl, onOpen }: EventCardProps) {
  // A card IS a date. A series with none of them left is not drawn at all,
  // rather than drawn against a date that is not there.
  const first = dates[0];
  if (!first) return null;
  const when = fmtWhen(first.start, first.end);
  const summary = (event.shortDescription || toPlainText(event.description ?? '')).trim();
  const more = dates.length - 1;
  // An event with a page of its own is linked to it: that page is the event,
  // with its ways in and its own registration. Everything else goes to the
  // shared details page by id.
  const href = event.pageUrl || `${detailsUrl}${first.id}`;

  function stop(e: MouseEvent) {
    e.stopPropagation();
  }

  const content = (
    <>
      <div className="relative aspect-video w-full overflow-hidden bg-gray-100">
        <EventArt
          event={event}
          date={first}
          className="transition-transform duration-500 group-hover:scale-[1.04]"
          sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw"
        />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          <StatusPills event={event} date={first} />
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-3 p-5">
        <p className="text-xs font-bold tracking-widest text-[#299c8f] uppercase">
          {when.day} · {when.time}
        </p>
        <h3 className="text-lg leading-tight font-extrabold tracking-tight text-[#1c2b39] uppercase">
          {event.title}
        </h3>
        {summary && <p className="line-clamp-2 text-sm leading-relaxed text-gray-600">{summary}</p>}
        <div className="flex items-center gap-1.5 text-sm text-gray-600">
          <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="truncate">{whereLine(event)}</span>
        </div>

        <div className="mt-auto flex flex-col gap-3 pt-1">
          <EventTags event={event} />
          <div className="flex items-center justify-between gap-3">
            <span className="text-xs font-semibold text-gray-500">
              {more > 0 ? `+ ${pluralDates(more)}` : ''}
            </span>
            {onOpen ? (
              <a
                href={href}
                onClick={stop}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#299c8f] px-4 py-2 text-sm font-bold text-white no-underline shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
              >
                {first.registrationOpen && !first.full ? 'Register' : 'Details'}
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </a>
            ) : (
              <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[#299c8f] px-4 py-2 text-sm font-bold text-white shadow-sm transition-all group-hover:scale-105 group-hover:shadow-md">
                {first.registrationOpen && !first.full ? 'Register' : 'Details'}
                <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );

  if (!onOpen) {
    return (
      <a href={href} className={CARD}>
        {content}
      </a>
    );
  }

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen(event, dates)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(event, dates);
        }
      }}
      className={CARD}
    >
      {content}
    </article>
  );
}
